import { Home, Settings, Box, Inbox, ChevronLeft, ChevronRight } from "lucide-react"
import { useState, useMemo, useEffect } from "react"
import { useIsMobile } from "../../hooks/use-mobile"
import { Link, useLocation } from "react-router-dom"
import { Tooltip } from "../molecules"

const NAVBAR_COLLAPSED_KEY = "navbar:collapsed"

const Navbar = () => {
	const isMobile = useIsMobile()
	const location = useLocation()

	// Initialize state from localStorage
	const [isCollapsed, setIsCollapsed] = useState(() => {
		const stored = localStorage.getItem(NAVBAR_COLLAPSED_KEY)
		return stored === "true"
	})

	// Persist state to localStorage whenever it changes
	useEffect(() => {
		localStorage.setItem(NAVBAR_COLLAPSED_KEY, String(isCollapsed))
	}, [isCollapsed])

	const items = useMemo(() => {
		return [
			{
				url: "/",
				icon: Home,
				title: "Home",
				active: location.pathname === "/",
				visible: true,
				section: "main"
			},
			{
				url: "/listings",
				icon: Inbox,
				title: "Listings",
				active: location.pathname === "/listings",
				visible: true,
				section: "main"
			},
			{
				url: "/inventory",
				icon: Box,
				title: "Inventory",
				active: location.pathname === "/inventory",
				visible: true,
				section: "main"
			},
			{
				url: "/settings",
				icon: Settings,
				title: "Settings",
				active: location.pathname === "/settings",
				visible: true,
				section: "bottom"
			},
		].filter((i) => i.visible)
	}, [location.pathname])

	const mainItems = items.filter(item => item.section === "main")
	const bottomItems = items.filter(item => item.section === "bottom")

	if (isMobile) {
		return (
			<div className="fixed bottom-0 left-0 w-full bg-white/70 dark:bg-neutral800/70 backdrop-blur-2xl border-t border-white/20 dark:border-neutral700/50 shadow-strong z-40 px-2 py-2 supports-[backdrop-filter]:bg-white/60 supports-[backdrop-filter]:dark:bg-neutral800/60">
				<div className="flex justify-around items-center max-w-lg mx-auto">
					{items.map((item) => (
						<Link
							to={item.url}
							key={item.url}
							className={`group relative flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all duration-300 ${item.active
								? "bg-gradient-to-br from-sumoPrimary to-sumoAmber text-sumoNavy shadow-lg shadow-sumoPrimary/30"
								: "text-neutral500 dark:text-neutral400 hover:bg-neutral100 dark:hover:bg-neutral700/50 hover:text-neutral900 dark:hover:text-neutral100 hover:scale-105"
								}`}
						>
							<div className={`transition-transform duration-300 ${item.active ? 'scale-110' : 'group-hover:scale-110'}`}>
								<item.icon width={20} strokeWidth={2.5} />
							</div>
							<span className="text-xs font-semibold">{item.title}</span>
							{item.active && (
								<div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-1 h-1 bg-sumoNavy rounded-full" />
							)}
						</Link>
					))}
				</div>
			</div>
		)
	}

	return (
		<div
			className={`relative bg-white/40 dark:bg-neutral800/40 backdrop-blur-2xl border-r border-white/20 dark:border-neutral700/30 shadow-soft p-3 flex flex-col transition-all duration-300 supports-[backdrop-filter]:bg-white/30 supports-[backdrop-filter]:dark:bg-neutral800/30 ${isCollapsed ? 'w-20' : 'w-64'}`}
			onClick={(e) => {
				// Prevent any click events on the sidebar container from expanding it
				e.stopPropagation()
			}}
		>
			{/* Collapse Toggle Button */}
			<button
				onClick={(e) => {
					e.stopPropagation()
					setIsCollapsed(!isCollapsed)
				}}
				className="absolute -right-3 top-8 z-10 w-6 h-6 bg-white/90 dark:bg-neutral800/90 backdrop-blur-md border border-white/30 dark:border-neutral700/50 rounded-full flex items-center justify-center hover:bg-white dark:hover:bg-neutral700 transition-all duration-200 shadow-md hover:shadow-lg"
				aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
			>
				{isCollapsed ? (
					<ChevronRight className="w-4 h-4 text-neutral600 dark:text-neutral400" />
				) : (
					<ChevronLeft className="w-4 h-4 text-neutral600 dark:text-neutral400" />
				)}
			</button>

			{/* Main Navigation Items */}
			<div className="flex flex-col gap-1 flex-grow">
				{mainItems.map((item) => {
					const linkElement = (
						<Link
							to={item.url}
							onClick={(e) => {
								// Prevent click from expanding sidebar - only navigate
								e.stopPropagation()
							}}
							className={`group relative flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-300 overflow-hidden ${item.active
								? "bg-gradient-to-r from-sumoPrimary to-sumoAmber text-sumoNavy shadow-lg shadow-sumoPrimary/20 scale-105"
								: "text-neutral600 dark:text-neutral300 hover:bg-gradient-to-r hover:from-neutral100 hover:to-neutral50 dark:hover:from-neutral700 dark:hover:to-neutral800 hover:text-neutral900 dark:hover:text-neutral100 hover:shadow-md hover:scale-105"
								}`}
						>
							{/* Active Indicator */}
							{item.active && (
								<div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-sumoNavy rounded-r-full" />
							)}

							{/* Icon with animation */}
							<div className={`relative z-10 transition-all duration-300 ${item.active ? 'scale-110' : 'group-hover:scale-110 group-hover:rotate-3'}`}>
								<item.icon width={20} strokeWidth={2.5} />
							</div>

							{/* Text with fade animation */}
							<span className={`relative z-10 whitespace-nowrap transition-all duration-300 ${isCollapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto'} ${item.active ? 'font-bold' : ''}`}>
								{item.title}
							</span>

							{/* Hover shimmer effect */}
							<div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
						</Link>
					)

					return isCollapsed ? (
						<Tooltip message={item.title} key={item.url}>
							{linkElement}
						</Tooltip>
					) : (
						<div key={item.url}>{linkElement}</div>
					)
				})}
			</div>

			{/* Divider */}
			{bottomItems.length > 0 && (
				<div className="my-2 border-t border-neutral200 dark:border-neutral700" />
			)}

			{/* Bottom Navigation Items */}
			<div className="flex flex-col gap-1">
				{bottomItems.map((item) => {
					const linkElement = (
						<Link
							to={item.url}
							onClick={(e) => {
								// Prevent click from expanding sidebar - only navigate
								e.stopPropagation()
							}}
							className={`group relative flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all duration-300 overflow-hidden ${item.active
								? "bg-gradient-to-r from-sumoPrimary to-sumoAmber text-sumoNavy shadow-lg shadow-sumoPrimary/20 scale-105"
								: "text-neutral600 dark:text-neutral300 hover:bg-gradient-to-r hover:from-neutral100 hover:to-neutral50 dark:hover:from-neutral700 dark:hover:to-neutral800 hover:text-neutral900 dark:hover:text-neutral100 hover:shadow-md hover:scale-105"
								}`}
						>
							{/* Active Indicator */}
							{item.active && (
								<div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-sumoNavy rounded-r-full" />
							)}

							{/* Icon with animation */}
							<div className={`relative z-10 transition-all duration-300 ${item.active ? 'scale-110' : 'group-hover:scale-110 group-hover:rotate-3'}`}>
								<item.icon width={20} strokeWidth={2.5} />
							</div>

							{/* Text with fade animation */}
							<span className={`relative z-10 whitespace-nowrap transition-all duration-300 ${isCollapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto'} ${item.active ? 'font-bold' : ''}`}>
								{item.title}
							</span>

							{/* Hover shimmer effect */}
							<div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
						</Link>
					)

					return isCollapsed ? (
						<Tooltip message={item.title} key={item.url} >
							{linkElement}
						</Tooltip>
					) : (
						<div key={item.url}>{linkElement}</div>
					)
				})}
			</div>
		</div>
	)
}

export default Navbar
